import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthStatusLayout from './components/auth/AuthStatusLayout';
import { logAuthEvent } from './lib/authLogger';
import { buildResultRoute, getSafeAuthType, sanitizeNextPath } from './lib/authUrls';
import { getSupabase, hasSupabaseConfig } from './lib/supabaseClient';

function buildFailureRoute(nextPath, reason = 'invalid_or_expired') {
  const safeNext = sanitizeNextPath(nextPath);
  const searchParams = new URLSearchParams({ reason });

  if (safeNext !== '/') {
    searchParams.set('next', safeNext);
  }

  return `/auth/error?${searchParams.toString()}`;
}

export default function AuthConfirmPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [statusMessage, setStatusMessage] = useState('Checking your confirmation link.');
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    let isActive = true;

    async function verifyConfirmation() {
      const tokenHash = searchParams.get('token_hash');
      const nextPath = sanitizeNextPath(searchParams.get('next'));
      const type = getSafeAuthType(searchParams.get('type'));

      if (!hasSupabaseConfig()) {
        logAuthEvent('error', '[auth.confirm] Supabase config missing', {
          hasTokenHash: Boolean(tokenHash),
          type,
        });
        navigate(buildFailureRoute(nextPath, 'auth_unavailable'), { replace: true });
        return;
      }

      if (!tokenHash || !type) {
        logAuthEvent('warn', '[auth.confirm] Invalid verification request', {
          hasTokenHash: Boolean(tokenHash),
          type,
          hasNext: nextPath !== '/',
        });
        navigate(buildFailureRoute(nextPath), { replace: true });
        return;
      }

      try {
        setStatusMessage('Verifying your email and activating your account.');

        const supabaseInstance = await getSupabase();
        const { error } = await supabaseInstance.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });

        if (error) {
          throw error;
        }

        if (!isActive) {
          return;
        }

        navigate(buildResultRoute('/email-confirmed', nextPath), { replace: true });
      } catch (error) {
        logAuthEvent('warn', '[auth.confirm] Verification failed', {
          type,
          hasNext: nextPath !== '/',
          errorMessage: error?.message || 'Unknown verifyOtp error',
          errorStatus: error?.status,
          errorCode: error?.code,
        });

        if (!isActive) {
          return;
        }

        navigate(buildFailureRoute(nextPath), { replace: true });
      }
    }

    verifyConfirmation();

    return () => {
      isActive = false;
    };
  }, [navigate, searchParams]);

  return (
    <AuthStatusLayout
      tone="success"
      title="Confirming your email."
      statusLabel="Verification in progress"
      icon={<ShieldCheck size={18} />}
    >
      <div className="auth-status__loading" aria-live="polite">
        <span className="auth-status__spinner" aria-hidden="true" />
        <span>{statusMessage}</span>
      </div>
    </AuthStatusLayout>
  );
}
